import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { formatCalendarDate } from 'src/app/shared/utils/date.utils';
import { SprayRecordService } from '../../../core/data-services/spray-record.service';
import { AdminSprayRecordSummary } from '../../../core/response-models/admin-spray-record-summary.model';

/**
 * Organization Spray Records Tab Component
 *
 * Displays a read-only paginated table of spray records for an organization.
 * Reads GET /admin/spray-records with the organizationId filter (SprayRecordService).
 * Row clicks navigate to the flight record detail page with a fromOrg query param.
 */
@Component({
  selector: 'app-organization-spray-records-tab',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './organization-spray-records-tab.component.html',
  styleUrls: ['./organization-spray-records-tab.component.scss']
})
export class OrganizationSprayRecordsTabComponent implements OnInit {
  @Input() organizationId!: string;

  records: AdminSprayRecordSummary[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;
  totalPages = 0;

  constructor(
    private sprayRecordService: SprayRecordService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!this.organizationId) {
      this.errorMessage = 'Organization ID is required';
      this.loading = false;
      return;
    }

    this.loadSprayRecords();
  }

  async loadSprayRecords(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const page = await firstValueFrom(this.sprayRecordService.getRecords({
        organizationId: this.organizationId,
        page: this.currentPage,
        pageSize: this.pageSize,
      }));
      this.records = page.items;
      this.totalRecords = page.paging.totalItems;
      this.totalPages = page.paging.totalPages;
    } catch (error: any) {
      console.error('Failed to load spray records:', error);
      this.errorMessage = 'Failed to load spray records. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  navigateToRecord(record: AdminSprayRecordSummary): void {
    this.router.navigate(['/flight-records', record.id], {
      queryParams: { fromOrg: this.organizationId }
    });
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadSprayRecords();
    }
  }

  getStatusBadgeClass(status: string | null): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'pending': return 'bg-warning text-dark';
      case 'cancelled': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusText(status: string | null): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      default: return status ?? '';
    }
  }

  /** Calendar-date safe — see shared/utils/date.utils.ts. */
  formatDate(date: string | null): string {
    return formatCalendarDate(date);
  }

  getChemicalNames(chemicals: AdminSprayRecordSummary['chemicals']): string {
    if (!chemicals || chemicals.length === 0) return '-';
    return chemicals.map(c => c.name).join(', ');
  }

  getDroneNames(droneNames: string[]): string {
    if (!droneNames || droneNames.length === 0) return '-';
    return droneNames.join(', ');
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }
}
